// src/features/admin/DoctorManagementPage.tsx
// Full doctor management: CRUD, schedules, leaves

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, ArrowLeft, Search, Loader2, Pencil, Trash2, CalendarOff,
  Calendar, Clock, Users, CheckCircle, XCircle, UserPlus, KeyRound, X,
} from 'lucide-react';
import { doctorAdminService, departmentService, staffAdminService } from '../../api/services';
import type {
  DoctorAdminDto, CreateDoctorRequest, UpdateDoctorRequest,
  ScheduleAdminDto, CreateScheduleRequest, DoctorLeaveDto, CreateLeaveRequest,
  DepartmentDto,
} from '../../api/types';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const CATEGORIES = ['Resident', 'Visiting', 'Consultant', 'InHouse'];

/* ═══════════════════════════════ MAIN PAGE ═══════════════════════════════ */

export function DoctorManagementPage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterActive, setFilterActive] = useState('');

  const { data: doctors, isLoading } = useQuery({
    queryKey: ['admin-doctors'],
    queryFn: doctorAdminService.getAll,
  });

  const { data: departments } = useQuery({
    queryKey: ['departments'],
    queryFn: departmentService.getAll,
  });

  const toggleMutation = useMutation({
    mutationFn: (id: number) => doctorAdminService.toggleActive(id),
    onSuccess: (d) => {
      toast.success(d.isActive ? 'Doctor activated' : 'Doctor deactivated');
      queryClient.invalidateQueries({ queryKey: ['admin-doctors'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const filtered = useMemo(() => {
    if (!doctors) return [];
    return doctors.filter((d) => {
      const q = search.toLowerCase();
      const matchSearch = !q || d.name.toLowerCase().includes(q)
        || d.departmentName.toLowerCase().includes(q)
        || (d.qualifications?.toLowerCase().includes(q));
      const matchDept = !filterDept || d.departmentName === filterDept;
      const matchActive = filterActive === '' || String(d.isActive) === filterActive;
      return matchSearch && matchDept && matchActive;
    });
  }, [doctors, search, filterDept, filterActive]);

  const uniqueDepts = useMemo(() => {
    if (!doctors) return [];
    return [...new Set(doctors.map((d) => d.departmentName))].sort();
  }, [doctors]);

  // ─── Detail/Edit view ──────
  if (selectedId !== null || isAdding) {
    return (
      <DoctorDetailView
        doctorId={selectedId}
        departments={departments ?? []}
        onBack={() => { setSelectedId(null); setIsAdding(false); }}
      />
    );
  }

  // ─── List view ──────
  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Doctor Management</h1>
          <p className="text-sm text-slate-500 mt-1">Add, edit, and manage doctor profiles, schedules, and leaves</p>
        </div>
        <button onClick={() => setIsAdding(true)} className="btn btn-primary">
          <UserPlus className="w-4 h-4 mr-2" /> Add Doctor
        </button>
      </div>

      {/* Filters */}
      <div className="card p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="search" placeholder="Search name, department, qualifications..." className="input pl-9 w-full"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="input w-44" value={filterDept} onChange={(e) => setFilterDept(e.target.value)}>
          <option value="">All Departments</option>
          {uniqueDepts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select className="input w-36" value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <span className="text-xs text-slate-500">{filtered.length} of {doctors?.length ?? 0}</span>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-600" /></div>
      ) : filtered.length === 0 ? (
        <div className="card p-12 text-center text-slate-500">
          <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">No doctors found</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">Doctor</th>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3 text-center">Schedules</th>
                <th className="px-4 py-3 text-center">Bookings</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((doc) => (
                <tr key={doc.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-bold shrink-0">
                        {doc.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-medium text-slate-800">{doc.title ? `${doc.title} ` : ''}{doc.name}</div>
                        <div className="text-xs text-slate-400">{doc.qualifications || 'No qualifications listed'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{doc.departmentName}</td>
                  <td className="px-4 py-3">
                    <span className={clsx('text-xs px-2 py-0.5 rounded-full font-medium',
                      doc.isOnCall ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                    )}>{doc.isOnCall ? 'On-Call' : doc.category}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{doc.schedules.length}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {doc.isAcceptingBookings
                      ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                      : <XCircle className="w-4 h-4 text-slate-300 mx-auto" />}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => toggleMutation.mutate(doc.id)}
                      className={clsx('text-xs px-2.5 py-1 rounded-full font-medium transition-colors',
                        doc.isActive ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-red-100 text-red-600 hover:bg-red-200'
                      )}
                    >{doc.isActive ? 'Active' : 'Inactive'}</button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => setSelectedId(doc.id)} className="btn btn-ghost btn-sm">
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════ DETAIL VIEW ═══════════════════════════════ */

function ResetDoctorPasswordModal({ doctorId, onClose }: { doctorId: number; onClose: () => void }) {
  const [newPassword, setNewPassword] = useState('');
  const [staffId, setStaffId] = useState<number | null>(null);
  const [noAccount, setNoAccount] = useState(false);

  useQuery({
    queryKey: ['staff-by-doctor', doctorId],
    queryFn: async () => {
      try {
        const s = await staffAdminService.getByDoctorId(doctorId);
        setStaffId(s.id);
        return s;
      } catch {
        setNoAccount(true);
        return null;
      }
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => staffAdminService.resetPassword(staffId!, { newPassword }),
    onSuccess: () => { toast.success('Password reset successfully'); onClose(); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="card w-full max-w-sm p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 btn btn-ghost btn-sm p-1">
          <X className="w-4 h-4" />
        </button>
        <h2 className="text-lg font-semibold text-slate-800 mb-1">Reset Doctor Password</h2>
        {noAccount ? (
          <div className="mt-3">
            <p className="text-sm text-slate-500 mb-4">No login account is linked to this doctor. Create a staff account with role "Doctor" first.</p>
            <button onClick={onClose} className="btn btn-secondary w-full">Close</button>
          </div>
        ) : staffId === null ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); resetMutation.mutate(); }} className="mt-3 space-y-4">
            <div>
              <label className="label">New Password</label>
              <input
                type="password"
                className="input w-full"
                required
                minLength={6}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </div>
            <button type="submit" className="btn btn-primary w-full" disabled={resetMutation.isPending}>
              {resetMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function DoctorDetailView({ doctorId, departments, onBack }: {
  doctorId: number | null;
  departments: DepartmentDto[];
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const isNew = doctorId === null;
  const [tab, setTab] = useState<'profile' | 'schedules' | 'leaves'>('profile');
  const [showResetPwd, setShowResetPwd] = useState(false);

  const { data: doctor, isLoading } = useQuery({
    queryKey: ['admin-doctor', doctorId],
    queryFn: () => doctorAdminService.getById(doctorId!),
    enabled: !isNew,
  });

  if (!isNew && isLoading) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-600" /></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      {showResetPwd && doctorId !== null && (
        <ResetDoctorPasswordModal doctorId={doctorId} onClose={() => setShowResetPwd(false)} />
      )}

      <button onClick={onBack} className="btn btn-ghost btn-sm mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to list
      </button>

      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-slate-800">
          {isNew ? 'Add New Doctor' : `${doctor?.title ?? ''} ${doctor?.name ?? ''}`}
        </h1>
        {!isNew && doctor && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowResetPwd(true)}
              className="btn btn-secondary btn-sm"
              title="Reset login password"
            >
              <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset Password
            </button>
            <span className={clsx('text-xs px-3 py-1 rounded-full font-medium',
              doctor.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'
            )}>{doctor.isActive ? 'Active' : 'Inactive'}</span>
          </div>
        )}
      </div>

      {/* Tabs (only for existing doctors) */}
      {!isNew && (
        <div className="flex gap-1 mb-5 border-b border-slate-200">
          {([['profile', 'Profile', Users], ['schedules', 'Schedules', Calendar], ['leaves', 'Leaves', CalendarOff]] as const).map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)}
              className={clsx('flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px',
                tab === id ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'
              )}>
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>
      )}

      <div className="card p-6">
        {(isNew || tab === 'profile') && (
          <DoctorProfileForm doctor={isNew ? null : doctor!} departments={departments}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['admin-doctors'] });
              if (isNew) onBack();
            }} />
        )}
        {!isNew && tab === 'schedules' && <DoctorSchedulesTab doctorId={doctorId!} />}
        {!isNew && tab === 'leaves' && <DoctorLeavesTab doctorId={doctorId!} />}
      </div>
    </div>
  );
}

/* ═══════════════════════════════ PROFILE FORM ═══════════════════════════════ */

function DoctorProfileForm({ doctor, departments, onSuccess }: {
  doctor: DoctorAdminDto | null;
  departments: DepartmentDto[];
  onSuccess: () => void;
}) {
  const isNew = doctor === null;
  const [form, setForm] = useState<Partial<CreateDoctorRequest>>(() =>
    doctor ? {
      name: doctor.name, title: doctor.title, qualifications: doctor.qualifications,
      departmentId: doctor.departmentId, category: doctor.category, gender: doctor.gender,
      experience: doctor.experience, shortBio: doctor.shortBio, fullBio: doctor.fullBio,
      photoUrl: doctor.photoUrl, consultationFee: doctor.consultationFee,
      followUpFee: doctor.followUpFee, languages: doctor.languages,
      phoneExtension: doctor.phoneExtension, visitingSchedule: doctor.visitingSchedule,
      associatedInstitution: doctor.associatedInstitution, appointmentNotes: doctor.appointmentNotes,
      displayOrder: doctor.displayOrder, isAcceptingBookings: doctor.isAcceptingBookings,
      isOnCall: doctor.isOnCall,
    } : { departmentId: departments[0]?.id, category: 'Resident', isAcceptingBookings: true, isOnCall: false, displayOrder: 0 }
  );

  const set = (key: string, value: unknown) => setForm((prev) => ({ ...prev, [key]: value }));

  const saveMutation = useMutation({
    mutationFn: () => isNew
      ? doctorAdminService.create(form as CreateDoctorRequest)
      : doctorAdminService.update(doctor!.id, form as UpdateDoctorRequest),
    onSuccess: () => { toast.success(isNew ? 'Doctor created' : 'Doctor updated'); onSuccess(); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
        <div>
          <label className="label">Name *</label>
          <input className="input w-full" required value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} />
        </div>
        <div>
          <label className="label">Title</label>
          <input className="input w-full" placeholder="Dr., Prof., etc." value={form.title ?? ''} onChange={(e) => set('title', e.target.value)} />
        </div>
        <div>
          <label className="label">Department *</label>
          <select className="input w-full" required value={form.departmentId ?? ''} onChange={(e) => set('departmentId', Number(e.target.value))}>
            <option value="">Select...</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Category *</label>
          <select className="input w-full" required value={form.category ?? ''} onChange={(e) => set('category', e.target.value)}>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Gender</label>
          <select className="input w-full" value={form.gender ?? ''} onChange={(e) => set('gender', e.target.value || undefined)}>
            <option value="">Not specified</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>
        <div>
          <label className="label">Experience</label>
          <input className="input w-full" placeholder="e.g. 15 years" value={form.experience ?? ''} onChange={(e) => set('experience', e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className="label">Qualifications</label>
          <input className="input w-full" placeholder="MBBS, MD, etc." value={form.qualifications ?? ''} onChange={(e) => set('qualifications', e.target.value)} />
        </div>
        <div>
          <label className="label">Consultation Fee</label>
          <input type="number" className="input w-full" value={form.consultationFee ?? ''} onChange={(e) => set('consultationFee', e.target.value ? Number(e.target.value) : undefined)} />
        </div>
        <div>
          <label className="label">Follow-up Fee</label>
          <input type="number" className="input w-full" value={form.followUpFee ?? ''} onChange={(e) => set('followUpFee', e.target.value ? Number(e.target.value) : undefined)} />
        </div>
        <div>
          <label className="label">Languages</label>
          <input className="input w-full" placeholder="English, Hindi, Kannada" value={form.languages ?? ''} onChange={(e) => set('languages', e.target.value)} />
        </div>
        <div>
          <label className="label">Display Order</label>
          <input type="number" className="input w-full" value={form.displayOrder ?? 0} onChange={(e) => set('displayOrder', Number(e.target.value))} />
        </div>
        <div className="md:col-span-2">
          <label className="label">Short Bio</label>
          <textarea className="input w-full" rows={2} value={form.shortBio ?? ''} onChange={(e) => set('shortBio', e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className="label">Appointment Notes</label>
          <input className="input w-full" placeholder="Special instructions for patients" value={form.appointmentNotes ?? ''} onChange={(e) => set('appointmentNotes', e.target.value)} />
        </div>

        {/* Toggles */}
        <div className="md:col-span-2 flex flex-wrap gap-6 pt-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-brand-600"
              checked={form.isAcceptingBookings ?? true} onChange={(e) => set('isAcceptingBookings', e.target.checked)} />
            <span className="text-sm text-slate-700">Accepting Bookings</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-brand-600"
              checked={form.isOnCall ?? false} onChange={(e) => set('isOnCall', e.target.checked)} />
            <span className="text-sm text-slate-700">On-Call / Visiting Doctor</span>
          </label>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button type="submit" disabled={saveMutation.isPending} className="btn btn-primary">
          {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {isNew ? 'Create Doctor' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}

/* ═══════════════════════════════ SCHEDULES TAB ═══════════════════════════════ */

function DoctorSchedulesTab({ doctorId }: { doctorId: number }) {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);

  const { data: schedules, isLoading } = useQuery({
    queryKey: ['admin-doctor-schedules', doctorId],
    queryFn: () => doctorAdminService.getSchedules(doctorId),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => doctorAdminService.deleteSchedule(id),
    onSuccess: () => { toast.success('Schedule removed'); queryClient.invalidateQueries({ queryKey: ['admin-doctor-schedules', doctorId] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-700">Weekly Schedule</h3>
        <button onClick={() => { setShowAdd(true); setEditId(null); }} className="btn btn-primary btn-sm">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Entry
        </button>
      </div>

      {showAdd && (
        <ScheduleForm doctorId={doctorId} onDone={() => setShowAdd(false)} />
      )}

      {editId !== null && (
        <ScheduleForm doctorId={doctorId} existing={schedules?.find((s) => s.id === editId)} onDone={() => setEditId(null)} />
      )}

      {!schedules?.length ? (
        <p className="text-sm text-slate-400 text-center py-8">No schedule entries yet</p>
      ) : (
        <table className="w-full text-sm mt-2">
          <thead>
            <tr className="text-xs text-slate-500 uppercase border-b">
              <th className="text-left px-3 py-2">Day</th>
              <th className="text-left px-3 py-2">Session</th>
              <th className="text-left px-3 py-2">Time</th>
              <th className="text-center px-3 py-2">Max Patients</th>
              <th className="text-center px-3 py-2">Slot (min)</th>
              <th className="text-center px-3 py-2">Active</th>
              <th className="text-center px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {schedules.map((s) => (
              <tr key={s.id} className="hover:bg-slate-50">
                <td className="px-3 py-2.5 font-medium text-slate-700">{s.dayName}</td>
                <td className="px-3 py-2.5">
                  <span className={clsx('text-xs px-2 py-0.5 rounded-full',
                    s.sessionType === 'Morning' ? 'bg-amber-50 text-amber-700' : 'bg-indigo-50 text-indigo-700'
                  )}>{s.sessionType}</span>
                </td>
                <td className="px-3 py-2.5 text-slate-600">{s.startTime} - {s.endTime}</td>
                <td className="px-3 py-2.5 text-center">{s.maxPatients}</td>
                <td className="px-3 py-2.5 text-center">{s.slotDurationMinutes}</td>
                <td className="px-3 py-2.5 text-center">
                  {s.isActive ? <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" /> : <XCircle className="w-4 h-4 text-slate-300 mx-auto" />}
                </td>
                <td className="px-3 py-2.5 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => { setEditId(s.id); setShowAdd(false); }} className="p-1.5 rounded hover:bg-slate-100">
                      <Pencil className="w-3.5 h-3.5 text-slate-500" />
                    </button>
                    <button onClick={() => deleteMutation.mutate(s.id)} className="p-1.5 rounded hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ScheduleForm({ doctorId, existing, onDone }: {
  doctorId: number;
  existing?: ScheduleAdminDto;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CreateScheduleRequest>(() => existing ? {
    dayOfWeek: existing.dayOfWeek,
    sessionType: existing.sessionType,
    startTime: existing.startTime,
    endTime: existing.endTime,
    maxPatients: existing.maxPatients,
    slotDurationMinutes: existing.slotDurationMinutes,
  } : { dayOfWeek: 1, sessionType: 'Morning', startTime: '09:00', endTime: '12:00', maxPatients: 20, slotDurationMinutes: 15 });

  const set = (key: string, value: unknown) => setForm((prev) => ({ ...prev, [key]: value }));

  const mutation = useMutation({
    mutationFn: () => existing
      ? doctorAdminService.updateSchedule(existing.id, form)
      : doctorAdminService.createSchedule(doctorId, form),
    onSuccess: () => {
      toast.success(existing ? 'Schedule updated' : 'Schedule added');
      queryClient.invalidateQueries({ queryKey: ['admin-doctor-schedules', doctorId] });
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
      <h4 className="text-sm font-semibold text-slate-700 mb-3">{existing ? 'Edit Schedule' : 'New Schedule Entry'}</h4>
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="label">Day</label>
          <select className="input" value={form.dayOfWeek} onChange={(e) => set('dayOfWeek', Number(e.target.value))}>
            {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Session</label>
          <select className="input" value={form.sessionType} onChange={(e) => set('sessionType', e.target.value)}>
            <option value="Morning">Morning</option>
            <option value="Evening">Evening</option>
          </select>
        </div>
        <div>
          <label className="label">Start</label>
          <input type="time" className="input" value={form.startTime} onChange={(e) => set('startTime', e.target.value)} />
        </div>
        <div>
          <label className="label">End</label>
          <input type="time" className="input" value={form.endTime} onChange={(e) => set('endTime', e.target.value)} />
        </div>
        <div>
          <label className="label">Max Patients</label>
          <input type="number" className="input w-24" min={1} value={form.maxPatients ?? 20} onChange={(e) => set('maxPatients', Number(e.target.value))} />
        </div>
        <div>
          <label className="label">Slot (min)</label>
          <input type="number" className="input w-24" min={5} value={form.slotDurationMinutes ?? 15} onChange={(e) => set('slotDurationMinutes', Number(e.target.value))} />
        </div>
        <div className="flex gap-2">
          <button onClick={() => mutation.mutate()} disabled={mutation.isPending} className="btn btn-primary btn-sm">
            {mutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : existing ? 'Update' : 'Add'}
          </button>
          <button onClick={onDone} className="btn btn-ghost btn-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════ LEAVES TAB ═══════════════════════════════ */

function DoctorLeavesTab({ doctorId }: { doctorId: number }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<CreateLeaveRequest>({ leaveDate: '', sessionType: undefined, reason: '' });

  const { data: leaves, isLoading } = useQuery({
    queryKey: ['admin-doctor-leaves', doctorId],
    queryFn: () => doctorAdminService.getLeaves(doctorId),
  });

  const addMutation = useMutation({
    mutationFn: () => doctorAdminService.createLeave(doctorId, form),
    onSuccess: () => {
      toast.success('Leave added');
      queryClient.invalidateQueries({ queryKey: ['admin-doctor-leaves', doctorId] });
      setForm({ leaveDate: '', sessionType: undefined, reason: '' });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => doctorAdminService.deleteLeave(id),
    onSuccess: () => { toast.success('Leave removed'); queryClient.invalidateQueries({ queryKey: ['admin-doctor-leaves', doctorId] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div>;

  return (
    <div>
      <h3 className="text-base font-semibold text-slate-700 mb-4">Leave Management</h3>

      {/* Add form */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-5">
        <h4 className="text-sm font-semibold text-slate-700 mb-3">Add Leave</h4>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="label">Date *</label>
            <input type="date" className="input" required value={form.leaveDate}
              onChange={(e) => setForm((p) => ({ ...p, leaveDate: e.target.value }))} />
          </div>
          <div>
            <label className="label">Session</label>
            <select className="input" value={form.sessionType ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, sessionType: e.target.value || undefined }))}>
              <option value="">Full Day</option>
              <option value="Morning">Morning Only</option>
              <option value="Evening">Evening Only</option>
            </select>
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="label">Reason</label>
            <input className="input w-full" placeholder="Optional" value={form.reason ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} />
          </div>
          <button onClick={() => addMutation.mutate()} disabled={!form.leaveDate || addMutation.isPending} className="btn btn-primary btn-sm">
            {addMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
            Add Leave
          </button>
        </div>
      </div>

      {/* List */}
      {!leaves?.length ? (
        <p className="text-sm text-slate-400 text-center py-8">No leave entries</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 uppercase border-b">
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Session</th>
              <th className="text-left px-3 py-2">Reason</th>
              <th className="text-left px-3 py-2">Created By</th>
              <th className="text-center px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {leaves.map((l) => {
              const isPast = l.leaveDate < new Date().toISOString().split('T')[0];
              return (
                <tr key={l.id} className={clsx('hover:bg-slate-50', isPast && 'opacity-50')}>
                  <td className="px-3 py-2.5 font-medium text-slate-700">{l.leaveDate}</td>
                  <td className="px-3 py-2.5">{l.sessionType ?? 'Full Day'}</td>
                  <td className="px-3 py-2.5 text-slate-500">{l.reason || '—'}</td>
                  <td className="px-3 py-2.5 text-slate-500 text-xs">{l.createdByName ?? '—'}</td>
                  <td className="px-3 py-2.5 text-center">
                    <button onClick={() => deleteMutation.mutate(l.id)} className="p-1.5 rounded hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
